import React, { useState, useEffect, useRef } from "react";
import ConfirmHeader from "../../components/ConfirmHeader";
import { isIntervalDueOn, intervalLabel } from "../../utils/habitInterval";
import { createPortal } from "react-dom";
import { Pencil, Trash2, Check, X, Plus, ChevronDown, Save } from "lucide-react";
import { S, CONFIRM_STACK, CONFIRM_DANGER, CONFIRM_CANCEL } from "../../theme/sharedStyles";
import { darken, lighten, isDarkBgTheme, vividPastel, onColor, DEFAULT_PRIMARY, isDarkMode, categoryInk, lightCategoryInk } from "../../theme/colorEngine";
import { GOAL_COLORS } from "../../constants/palettes";
import { DEFAULT_CATEGORIES } from "../../constants/icons";
import { uid } from "../../utils/storage";
import { todayStr, localDateStr } from "../../utils/dateHelpers";
import { pickCategoryColor } from "../../utils/categoryColor";
import { isHabitDueOn, isHabitDoneOn, isWithinHabitRange } from "../../utils/habitLogic";
import { isOccurringOn } from "../../utils/taskLogic";
import { formatReminders, durationLabel } from "../../utils/reminderFormat";
import { Card } from "../../components/Card";
import { CategoryIcon } from "../../components/CategoryIcon";
import { TabHeader } from "../../components/TabHeader";
import { EmojiPicker } from "../../components/pickers/EmojiPicker";
import { LucideIconPicker } from "../../components/pickers/IconPickers";
import { ModalColorPicker } from "../../components/pickers/ModalColorPicker";
import { AttachmentViewer } from "../../components/attachments/AttachmentViewer";
import { AddAmountSheet } from "../../components/AddAmountSheet";
import { isAmountHabit, formatDailyGoal } from "../../utils/habitUnits";
// Category icon colour: the colour darkened on light cards; in dark mode a light
// pastel of it, since a darkened colour vanished on the dark cards (Jade, 2026-10-17).
// New / Edit category panels: a white surface on every light background, not the
// accent tint that blended into the page (Jade, 2026-10-26). Dark mode keeps
// its raised surface.
const CAT_FORM_BG = () => (isDarkMode() ? "var(--c-bg-active)" : "#fff");
const iconInk = (hex, amt = 0.35) => (isDarkMode() ? categoryInk(hex) : darken(hex, amt));

export function CategoriesTab({ categories, setCategories, habits, setHabits, tasks, setTasks, habitLogs = {}, setHabitLogs, calEvents = [], setCalEvents = () => {}, habitSkips = [], setHabitSkips = () => {}, glassMode = false, masculineMode = false, openAddRef = null, isDesktop = false, savedColors = [], addSavedColor = () => {}, settingsOpen = false, primaryColor = DEFAULT_PRIMARY }) {
  // Card outer border — the app's current theme/accent color, lightened to a
  // pastel tint so it frames each block without competing with the category
  // label inside. One shared tint for every card, independent of each
  // category's own color (which still drives the header/content block).
  // 0.68 toward white read as plain white on a phone screen — 0.5 keeps it
  // pastel/light while staying visibly tinted.
  const cardBorderTint = lighten(primaryColor || DEFAULT_PRIMARY, 0.5);
  const [showForm, setShowForm] = useState(false);
  const [showDefaultPicker, setShowDefaultPicker] = useState(false);
  // Let a parent (the merged Dashboard) open the add-category form via the unified "+" menu
  if (openAddRef) openAddRef.current = () => { setShowDefaultPicker(false); setShowForm(true); };
  const [form, setForm] = useState({ emoji: "🎯", phosphorIcon: "", title: "", color: "", isGoal: false, goalDescription: "" });
  const addFormRef = useRef(null);
  const editFormRef = useRef(null);

  // Auto-scroll the add/edit panel into view — both render at the top of the tab,
  // so without this the user has to manually scroll up to see them after tapping
  // "+" or the edit pencil from further down the categories list.
  useEffect(() => {
    if (showForm) addFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [showForm]);
  const [editingCatId, setEditingCatId] = useState(null);
  const [editCatForm, setEditCatForm] = useState(null);
  // Category id whose today's-items check-off popup is open — tapping
  // anywhere on a category block (mobile and desktop alike) opens this;
  // Edit/Delete for the category itself live in the popup's header.
  const [checkoffFor, setCheckoffFor] = useState(null);
  // Which item (habit/task/event) is expanded inline inside the check-off
  // popup right now — accordion-style, one at a time. Resets whenever the
  // popup switches to a different category or closes.
  const [expandedItem, setExpandedItem] = useState(null); // { type, id } | null
  useEffect(() => { setExpandedItem(null); }, [checkoffFor]);
  // Category pending delete confirmation — whichever panel (the mobile
  // check-off popup, or desktop's expanded-in-place actions) triggered this
  // closes itself first, so only the confirm modal is on screen.
  const [confirmDeleteCat, setConfirmDeleteCat] = useState(null);

  const addCategory = () => {
    if (!form.title.trim()) return;
    // Respect a color the user explicitly picked. Otherwise auto-pick one no
    // other category is using — prefer a fresh palette hue; once all 12
    // distinct hues are taken, derive a tone-shifted variant (still unused) so
    // no two cards ever share the exact same color.
    const usedColors = categories.map(c => c.color);
    const autoColor = pickCategoryColor(usedColors, categories.length);
    const colorToUse = form.color || autoColor;
    setCategories(p => [...p, { id: uid(), ...form, color: colorToUse, createdAt: todayStr() }]);
    if (form.color) addSavedColor(form.color);
    setForm({ emoji: "🎯", phosphorIcon: "", title: "", color: "", isGoal: false, goalDescription: "" });
    setShowForm(false);
  };

  const deleteCategory = (id) => {
    setCategories(p => p.filter(c => c.id !== id));
  };

  const openEditCat = (c) => {
    setEditingCatId(c.id);
    setEditCatForm({ emoji: c.emoji || "🎯", lucideIcon: c.lucideIcon || "", phosphorIcon: c.phosphorIcon || "", _iconMode: !!(c.phosphorIcon), title: c.title || "", color: c.color || GOAL_COLORS[0], isGoal: !!c.isGoal, goalDescription: c.goalDescription || "" });
  };

  // Auto-scroll the edit panel into view too — opened from the pencil icon,
  // which can be tapped from anywhere in the (possibly long) category list.
  useEffect(() => {
    if (editingCatId) editFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [editingCatId]);

  const saveCat = () => {
    if (!editCatForm.title.trim()) return;
    if (editCatForm.color) addSavedColor(editCatForm.color);
    setCategories(p => p.map(c => c.id === editingCatId ? { ...c, ...editCatForm } : c));
    setEditingCatId(null);
    setEditCatForm(null);
  };

  const cancelEditCat = () => { setEditingCatId(null); setEditCatForm(null); };

  // Circle checkbox used in the mobile check-off popup — same idea as the
  // Habits tab's checkbox, kept local here since it's the only spot in
  // Spaces that needs one.
  const CircleCheck = ({ checked, color, onClick }) => {
    // Unchecked used to draw its ring at color + "55" (~33% alpha), which on
    // a white card left barely-there outlines — and unchecked is exactly the
    // state that needs to read as a tappable target. Full-strength ring,
    // saturated the same way the Habits tab's own checkboxes are.
    const ring = vividPastel(color);
    return (
      <button onClick={e => { e.stopPropagation(); onClick(); }} aria-label={checked ? "Mark not done" : "Mark done"}
        style={{
          width: 13, height: 13, borderRadius: "50%", flexShrink: 0, padding: 0,
          border: `2px solid ${ring}`,
          background: checked ? ring : "transparent",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", transition: "background 0.15s ease, border-color 0.15s ease",
        }}>
        {checked && <Check size={16} strokeWidth={3} color="#fff" />}
      </button>
    );
  };

  // Always the app theme's accent, never the category's own colour.
  const popupSectionLabel = (label) => (
    <p style={{ margin: "12px 0 5px", fontSize: 11, fontWeight: 800, color: "var(--c-text-accent)", textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</p>
  );

  // Habit/task/event detail fields, inline — used inside the check-off
  // popup's accordion expand (no separate detail modal; mobile and desktop
  // both open this same popup by tapping a category card).
  const DetailRow = ({ label, value }) => value ? (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, padding: "6px 0", borderBottom: "1px solid #f5eef8" }}>
      <span style={{ fontSize: 9, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</span>
      <span style={{ fontSize: 12.5, color: "var(--c-text)", lineHeight: 1.5 }}>{value}</span>
    </div>
  ) : null;

  const DetailFields = ({ type, item }) => (
    <>
      {type === "habit" && (
        <>
          <DetailRow label="Frequency" value={item.frequencyType === "daily" ? "Every day" : item.frequencyType === "interval" ? intervalLabel(item) : item.frequencyType === "weekly" ? `Weekly · ${(item.days || []).map(d => ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d]).join(", ")}` : item.frequencyType} />
          <DetailRow label="Type" value={item.habitType === "todo" ? "To-do" : item.habitType === "nottodo" ? "Not-to-do" : item.habitType === "limited" ? "Limited" : item.habitType} />
          {isAmountHabit(item)
            ? <DetailRow label="Goal" value={formatDailyGoal(item)} />
            : (item.dailyCount || 1) > 1 && <DetailRow label="Daily target" value={`${item.dailyCount}× per day`} />}
          <DetailRow label="Started" value={item.habitStartDate || item.createdAt?.slice(0,10)} />
          {item.quitReason && <DetailRow label="Reason" value={item.quitReason} />}
        </>
      )}
      {type === "task" && (
        <>
          <DetailRow label="Due" value={item.dueDate} />
          {item.time && <DetailRow label="Time" value={`${item.time}${item.endTime ? ` – ${item.endTime}` : ""}`} />}
          <DetailRow label="Status" value={item.done ? "✓ Completed" : "Pending"} />
          {item.notes && (
            <div style={{ display: "flex", flexDirection: "column", gap: 2, padding: "6px 0", borderBottom: "1px solid #f5eef8" }}>
              <span style={{ fontSize: 9, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 0.8 }}>Notes</span>
              <span className="clarity-notes-view" style={{ fontSize: 12.5, color: "var(--c-text)", lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: item.notes }} />
            </div>
          )}
          {item.subtasks?.length > 0 && <DetailRow label="Subtasks" value={item.subtasks.map(s => `${s.done ? "✓" : "·"} ${s.title}`).join(" · ")} />}
        </>
      )}
      {type === "event" && (
        <>
          {item.time && <DetailRow label="Time" value={`${item.time}${item.endTime ? ` – ${item.endTime}` : ""}${durationLabel(item.time, item.endTime) ? ` (${durationLabel(item.time, item.endTime)})` : ""}`} />}
          <DetailRow label="Date" value={item.date} />
          {item.location && <DetailRow label="Location" value={item.location} />}
          {formatReminders(item.reminders) && <DetailRow label="Reminders" value={formatReminders(item.reminders)} />}
          {(() => { const cc = categories.find(x => x.id === item.categoryId); return cc ? <DetailRow label="Category" value={`${cc.emoji ? cc.emoji + " " : ""}${cc.title}`} /> : null; })()}
          {item.description && <DetailRow label="Description" value={item.description} />}
          {item.notes && (
            <div style={{ display: "flex", flexDirection: "column", gap: 2, padding: "6px 0", borderBottom: "1px solid #f5eef8" }}>
              <span style={{ fontSize: 9, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 0.8 }}>Notes</span>
              <span className="clarity-notes-view" style={{ fontSize: 12.5, color: "var(--c-text)", lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: item.notes }} />
            </div>
          )}
          {item.repeat && item.repeat !== "None" && <DetailRow label="Repeat" value={item.repeat} />}
          {Array.isArray(item.attachments) && item.attachments.length > 0 && (
            <div style={{ padding: "6px 0" }}>
              <span style={{ fontSize: 9, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 0.8, display: "block", marginBottom: 6 }}>Attachments</span>
              <AttachmentViewer items={item.attachments} />
            </div>
          )}
        </>
      )}
    </>
  );

  const t = todayStr();

  // Compute today's stats across all categories
  const isRestrictedType = (h) => h.habitType === "nottodo" || h.habitType === "limited";

  // ── Completion checks + one-tap toggles (mirror the canonical logic
  //    used elsewhere so Spaces stays the single place to tick things off) ──
  const isHabitDone = (h) => {
    if (isRestrictedType(h)) return isHabitDoneOn(h, habitLogs, t, habitSkips);
    const val = habitLogs[t]?.[h.id];
    const target = h.dailyCount || 1;
    const current = typeof val === "number" ? val : (val ? 1 : 0);
    return current >= target;
  };

  // Unit habits (steps, glasses, …) are logged through the Add amount sheet.
  const [amountSheetId, setAmountSheetId] = useState(null);
  const toggleHabitDone = (h) => {
    if (isAmountHabit(h)) { setAmountSheetId(h.id); return; }
    const wasDone = isHabitDone(h);
    if (isRestrictedType(h)) {
      // Restricted habits aren't in habitLogs — "done" means no slip/skip today
      if (wasDone) {
        setHabits(prev => prev.map(x => x.id === h.id
          ? { ...x, relapseLog: [...(x.relapseLog || []), { date: t, note: "" }] } : x));
      } else {
        setHabits(prev => prev.map(x => x.id === h.id
          ? { ...x, relapseLog: (x.relapseLog || []).filter(r => r.date !== t) } : x));
        setHabitSkips(prev => prev.filter(s => !(s.habitId === h.id && s.skippedDate === t)));
      }
    } else {
      const target = h.dailyCount || 1;
      setHabitLogs(prev => {
        const cur = prev[t]?.[h.id];
        const isDone = typeof cur === "number" ? cur >= target : !!cur;
        return { ...prev, [t]: { ...(prev[t] || {}), [h.id]: isDone ? 0 : target } };
      });
    }
  };

  const toggleTaskDone = (tk) => {
    setTasks(prev => prev.map(x => x.id === tk.id
      ? { ...x, done: !x.done, completedAt: !x.done ? new Date().toISOString() : null } : x));
  };

  const toggleEventPassed = (ev) => {
    setCalEvents(prev => prev.map(x => x.id === ev.id ? { ...x, passed: !x.passed } : x));
  };

  const todayHabits = habits.filter(h => {
    if (!h.createdAt || t < localDateStr(h.createdAt)) return false;
    if (!isWithinHabitRange(h, t)) return false;
    if (isRestrictedType(h)) return true; // restricted always active
    if (h.frequencyType === "daily") return true;
    if (h.frequencyType === "specific" && h.dayLabels === "named") {
      const dow = new Date(t + "T00:00:00").getDay();
      const monBased = [6,0,1,2,3,4,5][dow];
      return (h.days || []).includes(monBased);
    }
    if (h.frequencyType === "count") return true;
    if (h.frequencyType === "interval") return isIntervalDueOn(h, t);
    if (h.frequencyType === "monthly") {
      if (h.monthlyMode === "count") return true;
      return (h.monthDates || []).includes(new Date(t + "T00:00:00").getDate());
    }
    return false;
  });
  const totalToday = todayHabits.length;
  const doneToday = todayHabits.filter(h => {
    if (isRestrictedType(h)) return isHabitDoneOn(h, habitLogs, t, habitSkips);
    const val = habitLogs[t]?.[h.id];
    const target = h.dailyCount || 1;
    const current = typeof val === "number" ? val : (val ? 1 : 0);
    return current >= target;
  }).length;
  const overallPct = totalToday === 0 ? 0 : Math.round((doneToday / totalToday) * 100);
  const activeCats = categories.filter(c => habits.some(h => h.goalId === c.id)).length;
  const bannerEmoji = overallPct === 100 ? "🎉" : overallPct >= 50 ? "🌸" : "✨";
  const bannerMsg = overallPct === 100
    ? "All done for today! Amazing!!"
    : totalToday === 0
    ? "Add some habits to get started~"
    : `${doneToday} of ${totalToday} habits done today`;

  const amountSheetHabit = amountSheetId ? habits.find(x => x.id === amountSheetId) : null;
  return (
    <div className={glassMode ? "cat-glass" : undefined} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {amountSheetHabit && (
        <AddAmountSheet habit={amountSheetHabit} rawLog={habitLogs[t]?.[amountSheetHabit.id]} dateLabel="Today"
          color={categories.find(c => c.id === amountSheetHabit.goalId)?.color || amountSheetHabit.color}
          onLog={(v) => setHabitLogs(prev => ({ ...prev, [t]: { ...(prev[t] || {}), [amountSheetHabit.id]: v } }))}
          onClose={() => setAmountSheetId(null)} />
      )}

      {/* Default Category Picker */}
      {showDefaultPicker && (
        <Card title="Choose a Starting Point" style={{ background: CAT_FORM_BG() }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 8,
            marginBottom: 12,
          }}>
            {DEFAULT_CATEGORIES.map(cat => (
              <button
                key={cat.title}
                onClick={() => {
                  setForm(p => ({ ...p, emoji: cat.emoji, title: cat.title, color: cat.color }));
                  setShowDefaultPicker(false);
                  setShowForm(true);
                }}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "center",
                  gap: 4, padding: "10px 6px",
                  background: cat.color + "33",
                  border: `1.5px solid ${cat.color}`,
                  borderRadius: 12, cursor: "pointer",
                  fontSize: 11, color: "var(--c-text)", fontWeight: 600,
                  lineHeight: 1.2, textAlign: "center",
                }}>
                <CategoryIcon cat={cat} size={22} color={iconInk(cat.color)} />
                {cat.title}
              </button>
            ))}
          </div>
          <button
            onClick={() => {
              setShowDefaultPicker(false);
              setForm({ emoji: "🎯", title: "", color: GOAL_COLORS[0], isGoal: false, goalDescription: "" });
              setShowForm(true);
            }}
            style={{ ...S.btn, width: "100%", textAlign: "center" }}>
            <Pencil size={13} style={{marginRight:6}} />Create Custom Category
          </button>
        </Card>
      )}

      {/* New Category Form */}
      {showForm && (
        <div ref={addFormRef}>
        <Card title="New Category" style={{ background: CAT_FORM_BG(), border: "none" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            {form._iconMode
              ? <LucideIconPicker value={form.phosphorIcon || ""} color={form.color || "#d4789a"} onChange={icon => setForm(p => ({ ...p, phosphorIcon: icon }))} onSwitch={() => setForm(p => ({ ...p, _iconMode: false, phosphorIcon: "", _openPicker: true }))} openOnMount={!!form._openPicker} />
              : <EmojiPicker value={form.emoji} onChange={e => setForm(p => ({ ...p, emoji: e }))} onSwitch={() => setForm(p => ({ ...p, _iconMode: true, _openPicker: true }))} openOnMount={!!form._openPicker} />
            }
            <input placeholder="Category name" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} style={{
              flex: 1, minWidth: 0, border: "none", borderBottom: "2px solid var(--c-border-mid)",
              borderRadius: 0, padding: "8px 2px", fontFamily: "inherit", fontSize: 20,
              fontWeight: 700, outline: "none", background: "transparent", boxSizing: "border-box",
              color: "var(--c-text)",
            }} />
          </div>

          <div style={{ marginBottom: 12 }}>
            <p style={S.label}>COLOR</p>
            <ModalColorPicker currentColor={form.color} onColorChange={c => setForm(p => ({ ...p, color: c }))} onSaveColor={addSavedColor} savedColors={savedColors} S={S} />
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={addCategory} className="clarity-add-btn" style={S.addBtn}><Save size={14} style={{marginRight:4}} />Save</button>
            <button onClick={() => { setShowForm(false); setShowDefaultPicker(false); }} style={S.btn}><X size={14} style={{marginRight:4}} />Cancel</button>
          </div>
        </Card>
        </div>
      )}

      {/* Edit Category Form */}
      {editingCatId && editCatForm && (
        <div ref={editFormRef}>
        <Card title="Edit Category" titleIcon={<Pencil size={15} />} style={{ background: CAT_FORM_BG(), border: "none" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            {editCatForm._iconMode
              ? <LucideIconPicker value={editCatForm.phosphorIcon || ""} color={editCatForm.color || "#d4789a"} onChange={icon => setEditCatForm(p => ({ ...p, phosphorIcon: icon }))} onSwitch={() => setEditCatForm(p => ({ ...p, _iconMode: false, phosphorIcon: "", _openPicker: true }))} openOnMount={!!editCatForm._openPicker} />
              : <EmojiPicker value={editCatForm.emoji} onChange={e => setEditCatForm(p => ({ ...p, emoji: e }))} onSwitch={() => setEditCatForm(p => ({ ...p, _iconMode: true, _openPicker: true }))} openOnMount={!!editCatForm._openPicker} />
            }
            <input placeholder="Category name" value={editCatForm.title} onChange={e => setEditCatForm(p => ({ ...p, title: e.target.value }))} style={{
              flex: 1, minWidth: 0, border: "none", borderBottom: "2px solid var(--c-border-mid)",
              borderRadius: 0, padding: "8px 2px", fontFamily: "inherit", fontSize: 20,
              fontWeight: 700, outline: "none", background: "transparent", boxSizing: "border-box",
              color: "var(--c-text)",
            }} />
          </div>

          <div style={{ marginBottom: 12 }}>
            <p style={S.label}>COLOR</p>
            <ModalColorPicker currentColor={editCatForm.color} onColorChange={c => setEditCatForm(p => ({ ...p, color: c }))} onSaveColor={addSavedColor} savedColors={savedColors} S={S} />
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={saveCat} className="clarity-add-btn" style={S.addBtn}><Save size={14} style={{marginRight:4}} />Save</button>
            <button onClick={cancelEditCat} style={S.btn}><X size={14} style={{marginRight:4}} />Cancel</button>
          </div>
        </Card>
        </div>
      )}

      {/* Section: My Categories */}
      <div>
        <div className="cat-section-header" style={{ marginBottom: 10 }}>
          <TabHeader
            title="Categories"
            gradient="linear-gradient(125deg, #d04080 0%, #e06030 100%)"
            subtextColor="#c03868"
            subtext=""
          />
        </div>
        <div className="cat-scroll" style={{
          overflowY: "auto",
          paddingRight: 2,
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}>
        <div className="cat-cards-grid" style={{ display: "grid", gap: 8 }}>
          {categories.map((c, _i) => {
            const catHabits = habits.filter(h => h.goalId === c.id);
            const catTasks  = tasks.filter(tk => tk.goalId === c.id);
            const col = c.color || "#d4789a";
            // The category container always renders the exact color the user picked
            // in Spaces — no theme re-toning (matches the donut chart on Clock-In,
            // which also renders category colors raw).
            const chrome = col;

            // Mobile: content block fill is the exact color the user picked,
            // full-strength (used below). Desktop instead keeps a gradient
            // header, so this is only read on mobile.
            const blockBg = glassMode
              ? `linear-gradient(150deg, ${col}f0 0%, ${col}d0 50%, ${col}e8 100%)`
              : chrome;
            // Text/icon color for content sitting directly on that full-color
            // block — picked for contrast whether the user's color is light or dark.
            const onBlock = onColor(chrome);
            const onBlockSoft = onBlock === "#ffffff" ? "rgba(255,255,255,0.72)" : "rgba(42,24,40,0.6)";

            // Today's habit completion — feeds the desktop header's progress bar.
            const catDone = catHabits.filter(h => {
              const val = habitLogs[t]?.[h.id];
              const target = h.dailyCount || 1;
              const current = typeof val === "number" ? val : (val ? 1 : 0);
              return current >= target;
            }).length;
            const catPct = catHabits.length === 0 ? 0 : Math.round((catDone / catHabits.length) * 100);

            const todayCatHabits = catHabits.filter(h => isHabitDueOn(h, t) && t >= (h.habitStartDate || h.createdAt || ""));
            const todayCatTasks  = catTasks.filter(tk => tk.dueDate === t || tk.startDate === t);
            const todayCatEvents = calEvents.filter(e => isOccurringOn(e, t) && e.categoryId === c.id);
            const hasAnything = todayCatHabits.length > 0 || todayCatTasks.length > 0 || todayCatEvents.length > 0;

            // Item list — the same plain bulleted, non-boxed, tap-anywhere
            // preview on both mobile and desktop; marking things done happens
            // in the check-off popup (opened by tapping the card), not here.
            // Only the color scheme differs by platform: mobile sits directly
            // on the full-color block (needs onColor() contrast), desktop sits
            // on a plain white body under its gradient header (fixed colors).
            // Desktop dark mode (Jade, 2026-10-18): the fixed light-card inks
            // above vanish on the dark card, so they follow the theme instead.
            // Dark gradient backgrounds on desktop (Jade, 2026-10-23): the card is
            // see-through like the habit cards beside it (cat-desk-glass, styled in
            // App.jsx's GLASS_CARD_CSS, which also turns the text vars white), so
            // it takes the same theme-following inks as dark mode.
            const deskGlass = isDesktop && !isDarkMode() && isDarkBgTheme();
            const deskDark = isDesktop && (isDarkMode() || deskGlass);
            const lightInk = deskGlass ? lightCategoryInk : categoryInk;
            const textColor  = isDesktop ? "var(--c-text-mid)" : onBlock;
            const taskColor  = deskDark ? "var(--c-text-mid)" : isDesktop ? "#3a5040" : onBlock;
            const eventColor = deskDark ? "var(--c-text-mid)" : isDesktop ? "#4a3070" : onBlock;
            const softColor  = deskDark ? "var(--c-text-soft)" : isDesktop ? "#c0a0b8" : onBlockSoft;
            const timeColor  = deskDark ? "var(--c-text-soft)" : isDesktop ? "#9a7ab8" : onBlockSoft;
            const labelInk   = deskDark ? lightInk(chrome) : null;
            // Phone, on a gradient background: the block's outer frame is a see-through
            // glass tint instead of white (Jade, 2026-10-24). No backdrop blur: on a
            // gradient it showed no difference, and blurring every block made scrolling
            // the tab lag on gradients only (Jade, same day). Default and dark mode keep
            // the white / card frame.

            const bulletDot = (accent) => (
              <span style={{ color: accent, fontSize: 15, lineHeight: 1, marginTop: 2, flexShrink: 0 }}>•</span>
            );
            const sectionLabel = (label, accent) => (
              <p style={{ margin: "5px 0 3px", fontSize: 10, fontWeight: 800, color: accent, textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</p>
            );

            const itemsList = (
              <div className="cat-card-items clarity-no-scrollbar" style={{
                height: 108, overflowY: "auto", display: "flex", flexDirection: "column",
                scrollbarWidth: "none", msOverflowStyle: "none",
              }}>
                {todayCatHabits.length > 0 && (
                  <>
                    {sectionLabel("Habits", labelInk || (isDesktop ? col : softColor))}
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {todayCatHabits.map(h => {
                        const done = isHabitDone(h);
                        return (
                          <div key={h.id} style={{ display: "flex", alignItems: "flex-start", gap: 6, padding: "2px 0" }}>
                            {bulletDot(isDesktop ? col : onBlockSoft)}
                            <span style={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: textColor, whiteSpace: "normal", lineHeight: 1.4, textDecoration: done ? "line-through" : "none", opacity: done ? 0.6 : 1 }}>{h.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {todayCatTasks.length > 0 && (
                  <>
                    {sectionLabel("Tasks", labelInk || (isDesktop ? "#5a9870" : softColor))}
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {todayCatTasks.map(tk => (
                        <div key={tk.id} style={{ display: "flex", alignItems: "flex-start", gap: 6, padding: "2px 0" }}>
                          {bulletDot(isDesktop ? "#5a9870" : onBlockSoft)}
                          <span style={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: taskColor, whiteSpace: "normal", lineHeight: 1.4, textDecoration: tk.done ? "line-through" : "none", opacity: tk.done ? 0.6 : 1 }}>{tk.title}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {todayCatEvents.length > 0 && (
                  <>
                    {sectionLabel("Events", labelInk || (isDesktop ? "#7b5ea8" : softColor))}
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {todayCatEvents.map(ev => {
                        const evAccent = ev.color || "#a07be8";
                        return (
                          <div key={ev.id} style={{ display: "flex", alignItems: "flex-start", gap: 6, padding: "2px 0" }}>
                            {bulletDot(isDesktop ? evAccent : onBlockSoft)}
                            <span style={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: eventColor, whiteSpace: "normal", lineHeight: 1.4, textDecoration: ev.passed ? "line-through" : "none", opacity: ev.passed ? 0.6 : 1 }}>{ev.title}</span>
                            {ev.time && <span style={{ fontSize: 10.5, color: timeColor, fontWeight: 600, flexShrink: 0 }}>{ev.time}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {!hasAnything && (
                  <p style={{ margin: "6px 0 2px", fontSize: 12, color: softColor, fontWeight: isDesktop ? 400 : 600, fontStyle: "italic" }}>Nothing scheduled today</p>
                )}
              </div>
            );

            return (
              <div key={c.id}
                className={`clarity-row-item${glassMode ? " glass-panel" : ""}${deskGlass ? " cat-desk-glass" : ""}`}
                onClick={() => setCheckoffFor(c.id)}
                style={{
                  borderRadius: isDesktop ? 18 : 20,
                  border: deskDark ? `1px solid ${chrome}33` : `1.5px solid ${glassMode ? "rgba(255,255,255,0.6)" : cardBorderTint}`,
                  background: deskDark ? "var(--c-bg-card)" : "#ffffff",
                  cursor: "pointer",
                  overflow: "hidden",
                  transition: "box-shadow 0.18s ease, transform 0.18s ease",
                  boxShadow: masculineMode ? "0 3px 14px rgba(40,55,80,0.14)" : "0 2px 8px rgba(180,80,120,0.07)",
                  animationDelay: `${_i * 0.035}s`,
                  display: "flex", flexDirection: "column",
                  padding: isDesktop ? 0 : 10, gap: isDesktop ? 0 : 8,
                }}>

                {isDesktop ? (
                  <>
                    {/* ── Header — flat color, always the user's exact pick,
                        no gradient and no theme-conditional washing. Full
                        solid strength (not an alpha tint) so it matches
                        mobile's plain full-color block instead of reading
                        as a washed-out pastel of the assigned color. ── */}
                    <div className="cat-desk-band" style={{
                      // Dark mode: a deep wash of the colour instead of the full
                      // pastel slab (bright fills glare on dark; research 2026-10-18).
                      background: deskDark ? `color-mix(in srgb, ${chrome} 20%, var(--c-bg-card))` : glassMode ? "transparent" : chrome,
                      padding: "12px 12px 10px",
                      display: "flex", flexDirection: "column", gap: 5,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div className="cat-desk-pill" style={{
                          display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1,
                          background: deskDark ? "transparent" : glassMode ? "rgba(255,255,255,0.72)" : "#fff",
                          borderRadius: 14,
                          padding: deskDark ? "2px 4px 2px 0" : "6px 14px 6px 6px",
                          boxShadow: deskDark ? "none" : `0 2px 8px ${chrome}33`,
                        }}>
                          <div style={{
                            width: 24, height: 24, borderRadius: "50%",
                            background: deskDark ? `${chrome}2e` : `${chrome}20`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            flexShrink: 0,
                          }}>
                            <CategoryIcon cat={c} size={15} color={deskGlass ? lightInk(chrome) : iconInk(chrome)} />
                          </div>
                          <p style={{ margin: 0, fontSize: 14.5, fontWeight: 800, color: deskDark ? "var(--c-text)" : "#2a1828", letterSpacing: "0.1px", lineHeight: 1.25, whiteSpace: "normal", wordBreak: "break-word", minWidth: 0 }}>{c.title}</p>
                        </div>
                        {c.isGoal && (
                          <span style={{ fontSize: 8, background: onBlock === "#ffffff" ? "rgba(255,255,255,0.28)" : "rgba(0,0,0,0.18)", color: onBlock, borderRadius: 5, padding: "2px 6px", fontWeight: 800, letterSpacing: "0.5px", flexShrink: 0 }}>GOAL</span>
                        )}
                      </div>
                      {catHabits.length > 0 && (
                        // Track/fill contrast against the header — now that the header is the
                        // solid assigned color (not a pale tint), a same-hue-alpha track/fill
                        // would blend invisibly into it, so these key off onBlock instead.
                        <div style={{ height: 3, borderRadius: 99, background: deskDark ? "rgba(255,255,255,0.12)" : onBlock === "#ffffff" ? "rgba(255,255,255,0.28)" : "rgba(0,0,0,0.18)", overflow: "hidden", marginTop: 2 }}>
                          <div style={{
                            height: "100%", width: `${catPct}%`, borderRadius: 99,
                            background: catPct === 100 ? "#6ee7b7" : deskDark ? chrome : onBlock,
                            transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)",
                          }} />
                        </div>
                      )}
                    </div>
                    <div className="cat-desk-body" style={{ padding: "4px 10px 10px" }}>
                      {itemsList}
                    </div>
                  </>
                ) : (
                  <>
                    {/* ── Mobile: full-color content block ── */}
                    <div style={{ background: blockBg, borderRadius: 14, padding: "10px 10px 8px" }}>
                      {itemsList}
                    </div>
                    {/* ── Footer — white strip carrying the category label ── */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "2px 2px 0" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        <div style={{
                          width: 24, height: 24, borderRadius: "50%",
                          background: `${chrome}20`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                        }}>
                          <CategoryIcon cat={c} size={15} color={iconInk(chrome)} />
                        </div>
                        <p style={{ margin: 0, fontSize: 14.5, fontWeight: 800, color: "var(--c-text)", letterSpacing: "0.1px", lineHeight: 1.25, whiteSpace: "normal", wordBreak: "break-word", minWidth: 0 }}>{c.title}</p>
                      </div>
                      {c.isGoal && (
                        <span style={{ fontSize: 8, background: chrome, color: "#fff", borderRadius: 5, padding: "2px 6px", fontWeight: 800, letterSpacing: "0.5px", flexShrink: 0 }}>GOAL</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Suggestions — below grid, revealed on scroll ── */}
        {(() => {
          const addedTitles = new Set(categories.map(c => c.title));
          const suggestions = DEFAULT_CATEGORIES.filter(dc => !addedTitles.has(dc.title));
          if (suggestions.length === 0) return null;
          // Desktop on a dark gradient: the see-through card look, like the
          // category cards above (Jade, 2026-10-23; styled in GLASS_CARD_CSS).
          const sugGlass = isDesktop && !isDarkMode() && isDarkBgTheme();
          const sugInk = (hex, amt) => (sugGlass ? lightCategoryInk(hex) : iconInk(hex, amt));
          return (
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid #f0e0ec" }}>
              <p style={{ margin: "0 0 10px", fontSize: 11, fontWeight: 800, color: isDarkBgTheme() ? "#ffffff" : "#000000", textTransform: "uppercase", letterSpacing: "0.07em" }}>More Categories</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {suggestions.map((cat) => {
                  const col = cat.color || "#d4789a";
                  return (
                    <button
                      key={cat.id}
                      className={sugGlass ? "cat-suggest-glass" : undefined}
                      onClick={() => setCategories(p => [...p, { ...cat, id: uid(), createdAt: todayStr() }])}
                      style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "10px 14px",
                        background: "var(--c-bg-card)",
                        border: `1.5px solid ${col}33`,
                        borderRadius: 14,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        textAlign: "left",
                        transition: "all 0.15s ease",
                      }}
                      // Hover only strengthens the edge. It used to swap the fill for a
                      // faint see-through tint, which darkened the row on a gradient.
                      onMouseEnter={e => { e.currentTarget.style.borderColor = `${col}88`; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = `${col}33`; }}
                    >
                      <div style={{
                        width: 32, height: 32, borderRadius: 9,
                        background: `${col}20`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                      }}>
                        <CategoryIcon cat={cat} size={18} color={sugInk(col)} />
                      </div>
                      <span title={cat.title} style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 700, color: "var(--c-text)", overflow: "hidden", textOverflow: "ellipsis" }}>{cat.title}</span>
                      <div style={{
                        display: "flex", alignItems: "center", gap: 4,
                        fontSize: 11, fontWeight: 700, color: sugInk(col, 0.15),
                        background: `${col}15`, borderRadius: 99,
                        padding: "3px 10px", flexShrink: 0,
                      }}>
                        <Plus size={11} />Add
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}

        </div>
      </div>

      {/* ── Check-off Popup (mobile only) — tapping anywhere on a category
          block opens this: today's habits/tasks/events for that category in
          one scrollable, centered popup. Habits/tasks get a circle checkbox;
          events are listed with no checkbox. Tapping an item's label opens
          the same detail panel as before; Edit/Delete for the category
          itself live in the popup header. ── */}
      {checkoffFor && (() => {
        const c = categories.find(x => x.id === checkoffFor);
        if (!c) return null;
        const close = () => setCheckoffFor(null);
        const col = c.color || "#d4789a";
        const catHabitsAll = habits.filter(h => h.goalId === c.id);
        const catTasksAll  = tasks.filter(tk => tk.goalId === c.id);
        const todayCatHabits = catHabitsAll.filter(h => isHabitDueOn(h, t) && t >= (h.habitStartDate || h.createdAt || ""));
        const todayCatTasks  = catTasksAll.filter(tk => tk.dueDate === t || tk.startDate === t);
        const todayCatEvents = calEvents.filter(e => isOccurringOn(e, t) && e.categoryId === c.id);
        const hasAnything = todayCatHabits.length > 0 || todayCatTasks.length > 0 || todayCatEvents.length > 0;

        const iconBtn = (onClick, color, Icon, label) => (
          <button onClick={onClick} aria-label={label} title={label} style={{
            background: "var(--c-bg-soft)", border: "none", borderRadius: 8, width: 28, height: 28,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            color, flexShrink: 0,
          }}><Icon size={13} /></button>
        );

        return createPortal(
          <>
            <div className="clarity-fade" style={{ position: "fixed", inset: 0, zIndex: 19998, background: "rgba(40,20,50,0.45)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }} onClick={close} />
            <div className="clarity-pop-c" style={{
              position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
              zIndex: 19999, width: "min(380px, calc(100vw - 32px))",
            }}>
              <div style={{
                background: "var(--c-bg-card)", borderRadius: 20,
                padding: "18px 18px 16px",
                boxShadow: "0 16px 48px rgba(60,20,80,0.28)",
                maxHeight: "calc(100dvh - 100px)",
                display: "flex", flexDirection: "column",
                fontFamily: "'Quicksand','Nunito',sans-serif",
              }} onClick={e => e.stopPropagation()}>

                {/* Header — icon, title, Edit/Delete for the category, Close */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexShrink: 0 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 10, background: `${col}22`, border: `1.5px solid ${col}55`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <CategoryIcon cat={c} size={17} color={iconInk(col)} />
                  </div>
                  <p style={{ flex: 1, minWidth: 0, margin: 0, fontSize: 15.5, fontWeight: 800, color: "var(--c-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.title}</p>
                  {iconBtn(() => { openEditCat(c); close(); }, "var(--c-text-soft)", Pencil, "Edit category")}
                  {iconBtn(() => { close(); setConfirmDeleteCat(c); }, "#e8647a", Trash2, "Delete category")}
                  {iconBtn(close, "var(--c-text-soft)", X, "Close")}
                </div>

                {/* Scrollable today's items — capped to roughly 5 items' worth
                    of height so anything beyond that scrolls instead of
                    growing the popup indefinitely. */}
                <div className="clarity-no-scrollbar" style={{ overflowY: "auto", maxHeight: 300, scrollbarWidth: "none", msOverflowStyle: "none" }}>
                  {todayCatHabits.length > 0 && (
                    <>
                      {popupSectionLabel("Habits")}
                      {todayCatHabits.map(h => {
                        const done = isHabitDone(h);
                        const isOpen = expandedItem?.type === "habit" && expandedItem.id === h.id;
                        return (
                          <div key={h.id} style={{ marginBottom: 2 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 2px" }}>
                              <CircleCheck checked={done} color={col} onClick={() => toggleHabitDone(h)} />
                              <span onClick={() => setExpandedItem(isOpen ? null : { type: "habit", id: h.id })} style={{ flex: 1, fontSize: 15.5, fontWeight: 600, color: "var(--c-text)", textDecoration: done ? "line-through" : "none", opacity: done ? 0.55 : 1, cursor: "pointer" }}>{h.name}</span>
                              <ChevronDown size={14} onClick={() => setExpandedItem(isOpen ? null : { type: "habit", id: h.id })} style={{ color: "var(--c-text-soft)", flexShrink: 0, cursor: "pointer", transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s ease" }} />
                            </div>
                            <div style={{ display: "grid", gridTemplateRows: isOpen ? "1fr" : "0fr", transition: "grid-template-rows 0.25s ease" }}>
                              <div style={{ overflow: "hidden" }}>
                                <div style={{ padding: "0 2px 8px 32px" }}><DetailFields type="habit" item={h} /></div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                  {todayCatTasks.length > 0 && (
                    <>
                      {popupSectionLabel("Tasks")}
                      {todayCatTasks.map(tk => {
                        const isOpen = expandedItem?.type === "task" && expandedItem.id === tk.id;
                        return (
                          <div key={tk.id} style={{ marginBottom: 2 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 2px" }}>
                              <CircleCheck checked={!!tk.done} color="#5a9870" onClick={() => toggleTaskDone(tk)} />
                              <span onClick={() => setExpandedItem(isOpen ? null : { type: "task", id: tk.id })} style={{ flex: 1, fontSize: 15.5, fontWeight: 600, color: "#3a5040", textDecoration: tk.done ? "line-through" : "none", opacity: tk.done ? 0.55 : 1, cursor: "pointer" }}>{tk.title}</span>
                              <ChevronDown size={14} onClick={() => setExpandedItem(isOpen ? null : { type: "task", id: tk.id })} style={{ color: "var(--c-text-soft)", flexShrink: 0, cursor: "pointer", transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s ease" }} />
                            </div>
                            <div style={{ display: "grid", gridTemplateRows: isOpen ? "1fr" : "0fr", transition: "grid-template-rows 0.25s ease" }}>
                              <div style={{ overflow: "hidden" }}>
                                <div style={{ padding: "0 2px 8px 32px" }}><DetailFields type="task" item={tk} /></div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                  {todayCatEvents.length > 0 && (
                    <>
                      {popupSectionLabel("Events")}
                      {todayCatEvents.map(ev => {
                        const isOpen = expandedItem?.type === "event" && expandedItem.id === ev.id;
                        const evAccent = ev.color || "#7b5ea8";
                        return (
                          <div key={ev.id} style={{ marginBottom: 2 }}>
                            <div onClick={() => setExpandedItem(isOpen ? null : { type: "event", id: ev.id })} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "7px 2px", cursor: "pointer" }}>
                              <span style={{ color: evAccent, fontSize: 15, lineHeight: 1, marginTop: 2, flexShrink: 0 }}>•</span>
                              <span style={{ flex: 1, fontSize: 15.5, fontWeight: 600, color: "#4a3070" }}>{ev.title}</span>
                              {ev.time && <span style={{ fontSize: 11, color: "#9a7ab8", fontWeight: 600, flexShrink: 0 }}>{ev.time}</span>}
                              <ChevronDown size={14} style={{ color: "#9a7ab8", flexShrink: 0, marginTop: 1, transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s ease" }} />
                            </div>
                            <div style={{ display: "grid", gridTemplateRows: isOpen ? "1fr" : "0fr", transition: "grid-template-rows 0.25s ease" }}>
                              <div style={{ overflow: "hidden" }}>
                                <div style={{ padding: "0 2px 8px 22px" }}><DetailFields type="event" item={ev} /></div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                  {!hasAnything && (
                    <p style={{ margin: "6px 0 2px", fontSize: 12.5, color: "#c0a0b8", fontStyle: "italic" }}>Nothing scheduled today</p>
                  )}
                </div>
              </div>
            </div>
          </>,
          document.body
        );
      })()}

      {/* ── Confirm Delete Category Modal — the check-off popup that opened
          this already closed itself first, so only this sits on screen. ── */}
      {confirmDeleteCat && createPortal(
        <>
          {/* No pop-in or fade: it just appears (Jade, 2026-10-24), like the other confirm panels. */}
          <div style={{ position: "fixed", inset: 0, zIndex: 19998, background: "rgba(40,20,50,0.45)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }} onClick={() => setConfirmDeleteCat(null)} />
          <div style={{
            position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            zIndex: 19999, width: "min(340px, calc(100vw - 40px))",
          }}>
            <div style={{
              background: "var(--c-bg-card)", borderRadius: 20,
              padding: "28px 24px",
              boxShadow: "0 16px 48px rgba(60,20,80,0.28)",
              textAlign: "center",
              fontFamily: "'Quicksand','Nunito',sans-serif",
            }} onClick={e => e.stopPropagation()}>
              <ConfirmHeader title="Are you sure you want to delete this category?" />
              <div style={CONFIRM_STACK}>
                <button onClick={() => setConfirmDeleteCat(null)} style={CONFIRM_CANCEL}>
                  Cancel
                </button>
                <button onClick={() => { deleteCategory(confirmDeleteCat.id); setConfirmDeleteCat(null); }}
                  style={CONFIRM_DANGER}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* ── FAB: Add Category ── (mobile Spaces tab only; on the desktop dashboard the
          unified add-FAB in HabitsTab covers both new habit + new category) */}
      {!showForm && !isDesktop && !settingsOpen && createPortal(
        <button
          data-tour="spaces-add-btn"
          onClick={() => { setShowForm(true); setShowDefaultPicker(false); }}
          title="New category"
          style={{
            position: "fixed", bottom: "calc(90px + var(--sab, 0px))", right: 24, zIndex: 99999,
            width: 56, height: 56, borderRadius: 18, border: "none",
            background: "linear-gradient(135deg, var(--c-primary-light) 0%, var(--c-primary-deep) 100%)",
            color: "var(--c-on-primary)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 18px rgba(180,80,160,0.45)",
            cursor: "pointer", transition: "transform 0.15s, box-shadow 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.08)"; e.currentTarget.style.boxShadow = "0 6px 24px rgba(180,80,160,0.55)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 18px rgba(180,80,160,0.45)"; }}
        >
          <Plus size={26} strokeWidth={2.5} />
        </button>,
        document.body
      )}


    </div>
  );
}
